import { useI18n } from '@ecom/i18n';
import { useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { useOverlay } from '../utils/use-overlay';

import { CloseIcon } from './CloseIcon';

export interface DrawerProps {
  readonly open: boolean;
  readonly title: string;
  readonly onClose: () => void;
  /** Pinned to the bottom of the panel, outside the scrolling body. */
  readonly footer?: ReactNode;
  /**
   * Edge the panel is anchored to, in logical terms: `end` (default — the cart, a detail) or `start` (a navigation
   * menu, which opens from the same side as the sidebar it replaces on small screens). Flips with `dir` on its own.
   */
  readonly side?: 'start' | 'end';
  readonly children: ReactNode;
}

/**
 * Side panel (§7.1 primitives), reproducing the storefront prototype's cart
 * drawer: full-height panel on the inline-end edge, `--ecom-surface`
 * background, full width on mobile and 26rem from the `sm` breakpoint,
 * scrim `rgba(10,16,14,.45)`.
 *
 * The prototype anchors it with `right-0` and slides it with
 * `translate-x-full`. Both are physical, and DEV §13 forbids that: under
 * `dir="rtl"` the cart would open from the wrong edge and animate the wrong
 * way. The anchor becomes `inset-inline-end: 0`, which flips on its own.
 * The *transform* cannot — `translateX` has no logical equivalent — so it is
 * the one place in this design system with an explicit `[dir='rtl']` rule
 * (see `components.css`), rather than a hidden per-component branch.
 *
 * Body scrolls independently of the header and footer so a long cart never
 * pushes the totals out of reach.
 */
export function Drawer({
  open,
  title,
  onClose,
  footer,
  side = 'end',
  children,
}: DrawerProps): ReactNode {
  const { t } = useI18n();
  const id = useId();
  const containerRef = useRef<HTMLDivElement>(null);

  useOverlay({ open, containerRef, onClose });

  if (!open) return null;

  const titleId = `${id}-title`;

  return createPortal(
    <div className="ecom-overlay">
      <div className="ecom-overlay__scrim" onClick={onClose} aria-hidden="true" />

      {/* A plain <div>, not the <aside> the prototype uses. `aside` carries an
          implicit `complementary` role, and overriding it with `dialog` is an
          invalid role combination — axe flags it as `aria-allowed-role`, and
          the a11y suite caught exactly that here. A dialog is not
          complementary content: it is the primary thing on screen while open. */}
      <div
        ref={containerRef}
        className="ecom-drawer"
        data-side={side}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <header className="ecom-drawer__header">
          <h2 id={titleId} className="ecom-drawer__title">
            {title}
          </h2>
          <button
            type="button"
            className="ecom-overlay__close"
            aria-label={t('common.close')}
            onClick={onClose}
          >
            <CloseIcon />
          </button>
        </header>

        <div className="ecom-drawer__body">{children}</div>

        {footer !== undefined ? <footer className="ecom-drawer__footer">{footer}</footer> : null}
      </div>
    </div>,
    document.body,
  );
}
